import React, { useCallback } from "react";
import "./style.css";
import Icon from "../Icon";
import useReveal from "../../hooks/useReveal";

/**
 * Variant keys name the accent they apply — the previous "dark" / "gradient" /
 * "clean" labels described neither the colour nor the layout.
 */
const services = [
  {
    icon: "brain",
    variant: "indigo",
    title: "Custom AI-Driven Software",
    description:
      "Large Language Models and custom machine learning applied to the work your team repeats every day.",
    features: [
      "LLM integration",
      "RAG pipelines",
      "Business automation",
      "AI agent systems",
    ],
  },
  {
    icon: "rocket",
    variant: "blue",
    title: "SaaS Product Development",
    description:
      "End-to-end product engineering from ideation and MVP to scale-ready production environments.",
    features: [
      "Scalable architecture",
      "Role-based systems",
      "Multi-tenant applications",
      "Cloud-native solutions",
    ],
  },
  {
    icon: "wrench",
    variant: "green",
    title: "Product Maintenance & Support",
    description:
      "Post-launch support, performance optimization, and continuous security patching for mission-critical apps.",
    features: [
      "24/7 monitoring",
      "Dedicated account manager",
      "Ticket-based support",
      "Continuous upgrades",
    ],
  },
];

export default function ServicesSection() {
  const [gridRef, gridVisible] = useReveal();

  /** Feeds pointer position to the card's .spotlight glow. */
  const onSpotlightMove = useCallback((e) => {
    const el = e.currentTarget;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--px", (e.clientX - rect.left) / rect.width);
    el.style.setProperty("--py", (e.clientY - rect.top) / rect.height);
  }, []);

  return (
    <section className="services-section">
      <div className="section-wrapper">
        <div className="container">
          {/* Section Header */}
          <div className="services-header">
            <h2 className="services-title">
              Engineering Intelligent Software for Growing Businesses
            </h2>
            <p className="services-subtitle">
              From custom AI systems to full SaaS platforms — we build software
              that scales with your business.
            </p>
          </div>

          {/* Services Cards */}
          <div className="row gx-4 gy-4 reveal-stagger" ref={gridRef}>
            {services.map((service, index) => (
              <div
                key={service.title}
                className={`col-lg-4 col-md-6 col-12 reveal ${
                  gridVisible ? "" : "is-hidden"
                }`}
                style={{ "--stagger-index": index }}
              >
                <div
                  className={`service-card service-card--${service.variant} spotlight icon-hover h-100`}
                  onPointerMove={onSpotlightMove}
                >
                  <div className="service-icon-wrap">
                    <Icon
                      name={service.icon}
                      size={30}
                      className="service-icon"
                    />
                  </div>
                  <h4 className="service-title">{service.title}</h4>
                  <p className="service-desc">{service.description}</p>
                  <div className="service-features">
                    {service.features.map((feature) => (
                      <span key={feature} className="feature-pill">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
